import { createContext, useContext, useEffect, useRef, useState } from "react";

const AuthContext = createContext(null);

const STORAGE_KEY = "oil_mgmt_session";

// Timeouts (em minutos) - sliding expiration
const TIMEOUT_MINUTES = {
  master: 30, // Master: sessão curta para operações críticas
  unidade: 480, // Unidade: 8h cobre um turno completo
};

const now = () => Date.now();
const addMinutes = (m) => now() + m * 60 * 1000;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  const clearSession = () => {
    setSession(null);
    sessionStorage.removeItem(STORAGE_KEY);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const persist = (data) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  const touch = () => {
    setSession((prev) => {
      if (!prev) return prev;
      const timeoutMin = TIMEOUT_MINUTES[prev.tipo] || 30;
      const next = { ...prev, expires_at: addMinutes(timeoutMin) };
      persist(next);
      return next;
    });
  };

  // Carrega sessão do sessionStorage (zera ao fechar aba/navegador)
  useEffect(() => {
    // Limpa sessões antigas armazenadas em localStorage (migração)
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.expires_at && parsed.expires_at > now()) {
          setSession(parsed);
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {
      // noop
    }
    setLoaded(true);
  }, []);

  // Timer: verifica expiração a cada 30s
  useEffect(() => {
    if (!session) return;
    timerRef.current = setInterval(() => {
      if (session.expires_at && session.expires_at <= now()) {
        clearSession();
      }
    }, 30 * 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [session]);

  // Listener de atividade para estender sessão (throttled via flag)
  useEffect(() => {
    if (!session) return;
    let pending = false;
    const handler = () => {
      if (pending) return;
      pending = true;
      // debounce: estende só a cada 60s para evitar spam
      setTimeout(() => {
        pending = false;
        touch();
      }, 60 * 1000);
    };
    const events = ["click", "keydown", "mousemove", "touchstart"];
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.tipo]);

  const login = (data) => {
    const timeoutMin = TIMEOUT_MINUTES[data.tipo] || 30;
    const enriched = { ...data, expires_at: addMinutes(timeoutMin) };
    setSession(enriched);
    persist(enriched);
  };

  const logout = () => {
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ session, login, logout, loaded, touch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
